import config from '../config';
import React, { useState, useEffect } from "react";
import { UserPlus } from "lucide-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "../style/memberList.css";
import { CircleUserRound } from "lucide-react";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";
import axios from "axios";

export default function MemberListing() {
  const { authData } = useAuth();
  const { organizationId } = useParams();
  const currentOrgId = organizationId || orgId || localStorage.getItem("currentOrgId");

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [userAvatar, setUserAvatar] = useState("");
  const [sortOrder, setSortOrder] = useState("A-Z");
  const location = useLocation();
  const { orgId, orgName } = location.state || "";
  console.log("values:", orgName, orgId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filteredMembers, setFilteredMembers] = useState([]);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  useEffect(() => {
    if (organizationId) {
      localStorage.setItem("currentOrgId", organizationId);
    }
  }, [organizationId]);
  
  useEffect(() => {
    const storedAvatar = localStorage.getItem("userAvatar");
    if (storedAvatar) {
      setUserAvatar(storedAvatar);
    }
  }, []);

  // Function to fetch image URL from the backend
  const fetchImageUrl = async (fileName) => {
    try {
      const response = await axios.get(
        `${config.BASE_URL}/get-image-url`,
        {
          params: {
            fileName: `${config.S3_BUCKET_FOLDER}/${fileName}`, // Append the bucket name prefix
          },
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        }
      );
      return response.data.presignedUrl; // Return the presigned URL
    } catch (error) {
      console.error("Error fetching image URL:", error);
      return null;
    }
  };

  useEffect(() => {
    async function fetchMembers() {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
          `${config.BASE_URL}/members/org/${organizationId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const fetchedMembers = await response.json();

        if (!Array.isArray(fetchedMembers)) {
          console.error("Fetched data is not an array:", fetchedMembers);
          setMembers([]);
          return;
        }

        // Fetch image URLs for members with profileFileName
        const membersWithImageUrls = await Promise.all(
          fetchedMembers.map(async (member) => {
            if (member.profileFileName) {
              const imageUrl = await fetchImageUrl(member.profileFileName);
              return { ...member, imageUrl };
            }
            return member;
          })
        );

        // Sort members by `createdAt`
        const sortedFetchedMembers = membersWithImageUrls
          .filter((member) => member.createdAt)
          .sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );

        setMembers(sortedFetchedMembers);
      } catch (error) {
        console.error("Error fetching members:", error);
        setError(error.message);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    }

    fetchMembers();
  }, [organizationId, authData.accessToken]);

  // Filter and sort members
  const filteredAndSortedMembers = members
  .filter((member) => {
  const q = search.toLowerCase().trim();
  
  if (!q) {
    return !showActiveOnly || member.isActive;
  }


  const terms = q.split(/\s+/);

  const first = member.firstName?.toLowerCase() || "";
  const last  = member.lastName?.toLowerCase()  || "";
  const role  = member.backpackEMRRole?.toLowerCase() || "";


  const matchesNameOrRole = terms.every(term =>
    first.includes(term) ||
    last.includes(term)  ||
    role.includes(term)
  );

  return matchesNameOrRole && (!showActiveOnly || member.isActive);
})
  .sort((a, b) => {
    const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
    const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
    
    if (sortOrder === "A-Z") {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  })

  useEffect(() => {
    if (showActiveOnly) {
      setFilteredMembers(members.filter((member) => member.isActive));
    } else {
      setFilteredMembers(members);
    }
  }, [showActiveOnly, members]);

  const clearSearch = () => setSearch("");

  const handleAddMember = (id) => {
    navigate(`/organization/${currentOrgId}/users/${id}/edit`, {
      state: { organizationId: currentOrgId, orgName },
    });
    
  };

  const handleBackClick = () => {
    navigate(`/organization/${organizationId}`);
  };

  return (
    <div className="ml-page">
      <div className="ml-content-wrapper">
        <div className="ml-header">
          <Button
            className="ml-back-button"
            onClick={handleBackClick}
            text="← Back"
            color="#006d7d"
          />
          <h1 className="ml-title">Members</h1>
        </div>

        {loading ? (
          <div className="ml-loading-container">
            <div className="ml-loader"></div>
            <p>Loading members...</p>
          </div>
        ) : error ? (
          <div className="ml-error-container">
            Error: {error}
          </div>
        ) : (
          <div className="ml-container">
            <div className="ml-controls">
              <input
                type="text"
                className="ml-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search for..."
              />
              <Button
                color={search ? "#006d7d" : "grey"}
                text="Clear"
                onClick={() => clearSearch()}
                className="ml-clear-btn"
              />
              <div className="ml-sort">
                <span className="ml-sort-label">Sort By:</span>
                <select
                  className="ml-sort-select"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="A-Z">A → Z</option>
                  <option value="Z-A">Z → A</option>
                </select>
              </div>

              <div className="ml-toggle">
                <span className="ml-toggle-label">Show All</span>
                <label className="ml-toggle-switch">
                  <input
                    type="checkbox"
                    className="ml-toggle-checkbox"
                    checked={showActiveOnly}
                    onChange={() => setShowActiveOnly(!showActiveOnly)}
                  />
                  <span className="ml-toggle-slider" />
                </label>
                <span className="ml-toggle-label">Active Only</span>
              </div>

              <button
                className="ml-add-button"
                onClick={() =>
                  navigate(`/organization/${currentOrgId}/users/add`, {
                    state: { organizationId: currentOrgId, orgName },
                  })
                }
              >
                <UserPlus size={17} style={{ marginRight: "8px" }} />
                <span>Add Member</span>
              </button>
            </div>

            <div className="ml-members">
              {filteredAndSortedMembers.length > 0 ? (
                filteredAndSortedMembers.map((member, index) => (
                  <div
                    key={index}
                    className="ml-member-item"
                    onClick={() => handleAddMember(member.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {member.imageUrl ? (
                      <img
                        src={member.imageUrl}
                        alt="Avatar"
                        className="ml-member-avatar"
                      />
                    ) : (
                      <CircleUserRound className="ml-member-avatar" />
                    )}
                    <div className="ml-member-info">
                      <strong>
                        {`${member.firstName || "Unknown"} ${member.lastName || ""
                          }`.trim()}
                      </strong>{" "}
                      <small>{member.organizationRole || "Unknown Role"}</small>{" "}
                    </div>
                  </div>
                ))
              ) : (
                <div className="ml-no-members">
                  No members found.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}